import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";

import {
  Box,
  Grid,
  Paper,
  Typography,
  Container,
  TextField,
  Stack,
  FormControl,
  Select,
  Checkbox,
  InputLabel,
  ListItemText,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  MenuItem,
  CircularProgress,
  AppBar,
  Button,
} from "@mui/material";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SyncIcon from "@mui/icons-material/Sync";

import { obtenerDatosDeProvedoresOptimizado } from "../../APIS/apiProveedor";

// Modales
import AgregarViajeProveedor from "./Proveedor/AgregarViajeProveedor";
import AgregarVehiculoProveedor from "./Proveedor/AgregarVehiculoProveedor";
import AgregarPaxProveedor from "./Proveedor/AgregarPaxProveedor";
import AgregarNoShowProveedor from "./Proveedor/AgregarNoShowProveedor";
import AgregarExtraProveedor from "./Proveedor/AgregarExtraProveedor";
import AgregarFechaHora from "./Proveedor/AgregarFechaHora";
import ModificarConductor from "./Proveedor/ModificarConductor";

import Image from "mui-image";
import Logo from "../Imagenes/logo_morado_texto_al_lado.png";
import * as XLSX from "xlsx";

/* ----------------- utilidades ------------------ */
const normalizeText = (str: any = "") =>
  String(str ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const Estado = [
  { id: 1, label: "Pendiente" },
  { id: 2, label: "Cancelado" },
  { id: 3, label: "Proximamente" },
  { id: 4, label: "Activo" },
  { id: 5, label: "En curso" },
  { id: 6, label: "Completado" },
  { id: 7, label: "Por Validar" },
];

const getBackgroundColor = (label: string) => {
  switch (label) {
    case "Pendiente":
      return "#616161";
    case "Cancelado":
      return "#F44336";
    case "Cancelado API":
      return "#E91E63";
    case "Proximamente":
      return "#3F51B5";
    case "Activo":
      return "#2196F3";
    case "En curso":
      return "#FFC107";
    case "Completado":
      return "#4CAF50";
    case "Por Validar":
      return "#9C27B0";
    default:
      return "transparent";
  }
};

const estadosOpciones = [
  "Pendiente",
  "Proximamente",
  "Activo",
  "En curso",
  "Por Validar",
];

/* ------------ hook para parpadeo de fila ---------------- */
const useBlinkingStyle = () => {
  const [blinking, setBlinking] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setBlinking((p) => !p), 500);
    return () => clearInterval(t);
  }, []);
  return blinking ? { backgroundColor: "rgba(255,0,0,0.2)" } : {};
};

/* ---------- helpers hora local ---------- */
function parseLocalDateTime(fecha: string, hora: string) {
  return moment(`${fecha} ${hora}`, "YYYY-MM-DD HH:mm");
}

function isStartingWithin30Minutes(viajes: any[]) {
  if (!viajes?.length) return false;
  const now = moment();
  const futuros = viajes.filter((v) =>
    parseLocalDateTime(v.Fecha, v.Hora).isAfter(now)
  );
  if (!futuros.length) return false;
  const proximo = futuros.reduce((acc, cur) =>
    parseLocalDateTime(cur.Fecha, cur.Hora).isBefore(
      parseLocalDateTime(acc.Fecha, acc.Hora)
    )
      ? cur
      : acc
  );
  const diff = parseLocalDateTime(proximo.Fecha, proximo.Hora).diff(
    now,
    "minutes"
  );
  // Nota: aquí tu umbral es 45 min (aunque el nombre diga 30)
  return diff >= 0 && diff <= 45;
}

/* =========================================================
   COMPONENTE PRINCIPAL
   ========================================================= */
export function PanelDeControlProvee() {
  const navigate = useNavigate();

  /* ------------ estado ------------ */
  const [counter, setCounter] = useState("");
  const [estados, setEstados] = useState(estadosOpciones);
  const [buscador, setBuscador] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const editingRowIdRef = useRef(null);
  const unsubscribeRef = useRef();

  const fetchDataRef = useRef(() => {});

  /* ---------- autenticación ---------- */
  useEffect(() => {
    if (!localStorage.getItem("isAuthenticated")) navigate("/login");
    const beforeUnload = () =>
      ["isAuthenticated", "counter", "firstname"].forEach((k) =>
        localStorage.removeItem(k)
      );
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [navigate]);

  useEffect(() => {
    const stored = localStorage.getItem("counter");
    if (stored) setCounter(stored);
  }, []);

  /* ---------- fechas ---------- */
  const [fechaInicio, setFechaInicio] = useState(
    moment().subtract(1, "days").format("YYYY-MM-DD")
  );
  const [fechaTermino, setFechaTermino] = useState(
    moment().add(1, "days").format("YYYY-MM-DD")
  );
  const fechaMaximaInicio = moment()
    .subtract(2, "months")
    .format("YYYY-MM-DD");

  const fechaActual = moment().format("YYYY-MM-DD");

  /* --------------------------------------------------------
     fetchData: única puerta de entrada a Firestore
     -------------------------------------------------------- */
  fetchDataRef.current = () => {
    if (!counter) return;
    if (unsubscribeRef.current) unsubscribeRef.current();

    setLoading(true);
    unsubscribeRef.current = obtenerDatosDeProvedoresOptimizado(
      counter,
      fechaInicio,
      fechaTermino,
      estados,
      (data: any[]) => {
        const safe = editingRowIdRef.current
          ? data.filter((r) => r.id !== editingRowIdRef.current)
          : data;
        setRows(safe);
        setLoading(false);
      }
    );
  };

  /* primera carga cuando aparece counter */
  useEffect(() => {
    if (counter) fetchDataRef.current();
  }, [counter]);

  /* limpia subscripción al desmontar */
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);

  /* -------------- filtrado local -------------- */
  const filterData = useCallback((data: any[], texto: string) => {
    const query = normalizeText(texto);
    if (!query) return data;

    const tokens = query.split(/\s+/).filter(Boolean);
    const deepIncludes = (obj: any): boolean => {
      for (const key in obj) {
        const val = obj[key];
        if (val && typeof val === "object") {
          if (deepIncludes(val)) return true;
        } else if (
          val != null &&
          tokens.every((tok) => normalizeText(val).includes(tok))
        ) {
          return true;
        }
      }
      return false;
    };
    return data.filter(deepIncludes);
  }, []);

  const visibleRows = useMemo(
    () => filterData(rows, buscador),
    [rows, buscador, filterData]
  );

  /* -------------- Excel export -------------- */
  const handleOnExportListado = () => {
    // ⚠️ Si prefieres exportar solo lo visible, cambia "rows" por "visibleRows"
    const source = rows;

    const sheetData = source.flatMap(({ Datos, DatosProveedor, Viaje = [] }) =>
      Viaje.map((viaje: any, idx: number) => {
        const row: any = {
          Aerolinea: Datos?.aerolinea ?? "N/A",
          Aeropuerto: Datos?.aeropuerto ?? "N/A",
          Distritos: Datos?.cDistritos ?? "N/A",
          NRT: Datos?.nrt ?? "N/A",
          Viaje: idx === 0 ? "Zarpe" : idx === 1 ? "Reco" : "Extras",
          "Número de Vuelo": Datos?.nVuelo ?? "N/A",
          Pax: Datos?.pax ?? "N/A",
          ParR: viaje?.PaxR ?? "N/A",
          Origen: viaje?.Origen ?? "N/A",
          Destino: viaje?.Destino ?? "N/A",
          Estado: viaje?.Estado ?? "N/A",
          Fecha: viaje?.Fecha ?? "N/A",
          Hora: viaje?.Hora ?? "N/A",
          "Proveedor - Tipo de Vehículo":
            DatosProveedor?.tipoDeVehiculoPro ?? "N/A",
          Observaciones: "",
        };

        if (Datos?.nrt?.includes("DPSCL")) {
          row["Total"] = Datos?.total ?? "N/A";
        }

        return row;
      })
    );

    if (!sheetData.length) {
      console.log("No hay datos para exportar.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, "Listado");
    XLSX.writeFile(wb, "Listado_de_Reportes.xlsx");
  };

  /* ----------- actualizar fila modificada ----------- */
  const handleUpdateRow = (nrtId: string, idViaje: string, newData: any) => {
    setRows((prev) =>
      prev.map((row) =>
        row.Datos?.nrt !== nrtId
          ? row
          : {
              ...row,
              Viaje: row.Viaje.map((v: any) =>
                v.idd === idViaje ? { ...v, ...newData } : v
              ),
            }
      )
    );
    editingRowIdRef.current = null;
  };

  /* ===================================================
     RENDER
     =================================================== */
  return (
    <Box component="main" sx={{ flexGrow: 1 }}>
      {/* ---------- barra superior ---------- */}
      <AppBar
        position="static"
        sx={{
          bgcolor: "#8c52fe",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 600,
            textAlign: "center",
            p: 1,
          }}
        >
          <Image
            src={Logo}
            alt="Logo"
            style={{ width: "100%", maxWidth: 250, cursor: "pointer" }}
            onClick={() => navigate("/PortalProveedor")}
          />
        </Box>
      </AppBar>

      <Container maxWidth="xxl" sx={{ mt: 3 }}>
        <Stack spacing={0}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              width: "100%",
              textAlign: "center",
              bgcolor: "rgb(17,25,42)",
              color: "white",
              borderRadius: 3,
              py: 1,
            }}
          >
            PANEL DE SOLICITUDES
          </Typography>
        </Stack>

        {/* ------------ filtros ------------ */}
        <Grid container spacing={1} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Buscador (completo)"
              fullWidth
              value={buscador}
              onChange={(e) => setBuscador(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              label="Proveedor"
              fullWidth
              value={counter}
              InputLabelProps={{ shrink: true }}
              error={!counter.trim()}
              helperText={!counter.trim() && "Este campo no puede estar vacío"}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                multiple
                value={estados}
                onChange={(e) => setEstados(e.target.value)}
                input={<OutlinedInput label="Estado" />}
                renderValue={(sel) => (sel).join(", ")}
              >
                {Estado.map(({ id, label }) => (
                  <MenuItem
                    key={id}
                    value={label}
                    style={{ backgroundColor: getBackgroundColor(label) }}
                  >
                    <Checkbox checked={estados.indexOf(label) > -1} />
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              type="date"
              label="Fecha Inicio"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fechaMaximaInicio, max: fechaActual }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              type="date"
              label="Fecha Término"
              fullWidth
              value={fechaTermino}
              onChange={(e) => setFechaTermino(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fechaInicio }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant="contained"
              fullWidth
              sx={{
                height: 57.5,
                bgcolor: "#8c52fe",
                color: "white",
                "&:hover": {
                  bgcolor: "white",
                  color: "#8c52fe",
                  border: "1px solid #8c52fe",
                },
              }}
              onClick={fetchDataRef.current}
            >
              Actualizar&nbsp;<SyncIcon />
            </Button>
          </Grid>

          {/* botón Excel */}
          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                height: 57.5,
                bgcolor: "#8c52fe",
                color: "white",
                "&:hover": {
                  bgcolor: "white",
                  color: "#8c52fe",
                  border: "1px solid #8c52fe",
                },
              }}
              onClick={handleOnExportListado}
            >
              Excel&nbsp;<FileDownloadIcon />
            </Button>
          </Grid>
        </Grid>

        {/* ------------ tabla ------------ */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={80}>Estados</TableCell>
                  <TableCell width={80}>ID</TableCell>
                  <TableCell width={80}>N° Vuelo</TableCell>
                  <TableCell width={80}>Pax</TableCell>
                  <TableCell width={80}>V/A</TableCell>
                  <TableCell width={80}>Agregar Viaje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, i) => (
                  <Row
                    key={row?.Datos?.nrt ?? i}
                    Mapeo={row}
                    setIsEditing={() => {}}
                    onUpdateRow={handleUpdateRow}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
}

/* ----------------------------------------------------------------------------
   Row: Componente fila expandible
---------------------------------------------------------------------------- */
function Row({
  Mapeo,
  setIsEditing,
  onUpdateRow,
}: {
  Mapeo: any;
  setIsEditing: (v: boolean) => void;
  onUpdateRow: (nrtId: string, idViaje: string, newData: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const [openFechaHora, setOpenFechaHora] = useState(false);
  const [openAgregar, setOpenAgregar] = useState(false);
  const [openPax, setOpenPax] = useState(false);
  const [openNoShow, setOpenNoShow] = useState(false);
  const [openExtras, setOpenExtras] = useState(false);
  const [openVehiculo, setOpenVehiculo] = useState(false);
  const [openConductor, setOpenConductor] = useState(false);
  const [Idd, setIdd] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [hidden, setHidden] = useState(false);

  // Hook de parpadeo
  const blinkingStyle = useBlinkingStyle();

  // Determina si la fila debe parpadear (faltan <= 45 minutos para algún viaje)
  const shouldBlink =
    isStartingWithin30Minutes(Mapeo.Viaje) &&
    !Mapeo.Viaje.some((viaje: any) => viaje.Estado === "Completado") &&
    !hidden;

  // Aplica el estilo de parpadeo a la fila completa
  const rowStyle = shouldBlink ? blinkingStyle : {};

  const primerViaje = Mapeo.Viaje?.find(
    (historyRow: any) =>
      historyRow.Estado === "Activo" ||
      historyRow.Estado === "En curso" ||
      historyRow.Estado === "Proximamente"
  );

  // Ejemplo: refresco automático (opcional)
  useEffect(() => {
    const interval = setInterval(() => {
      setRefresh((prev) => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const convertFirestoreTimestampToDate = (timestamp: any) => {
    if (!timestamp) return null;
    return new Date(timestamp.seconds * 1000);
    // Si timestamp ya viene como Date, ajusta esta función
  };

  const isNewService = (fechaCounterIngresada: any) => {
    const fecha = convertFirestoreTimestampToDate(fechaCounterIngresada);
    if (!fecha) return false;
    return moment().diff(fecha, "minutes") <= 30;
  };

  const getTrafficLightColor = (fechaCounterIngresada: any) => {
    const fecha = convertFirestoreTimestampToDate(fechaCounterIngresada);
    if (!fecha) return null;
    const diffInMinutes = moment().diff(fecha, "minutes");
    if (diffInMinutes < 5) return "green";
    if (diffInMinutes < 10) return "yellow";
    return "red";
  };

  // Abrir/Cerrar modales
  const handleOpenViaje = () => {
    setIsEditing(true);
    setOpenAgregar(true);
  };
  const handleCloseViaje = () => {
    setIsEditing(false);
    setOpenAgregar(false);
  };

  const handleOpenFechaHora = (idd: string) => {
    setIsEditing(true);
    setIdd(idd);
    setOpenFechaHora(true);
  };
  const handleCloseFechaHora = () => {
    setIsEditing(false);
    setOpenFechaHora(false);
  };

  const handleOpenVehiculo = (idd: string) => {
    setIsEditing(true);
    setIdd(idd);
    setOpenVehiculo(true);
  };
  const handleCloseVehiculo = () => {
    setIsEditing(false);
    setOpenVehiculo(false);
  };

  const handleOpenPax = (idd: string) => {
    setIsEditing(true);
    setIdd(idd);
    setOpenPax(true);
  };
  const handleClosePax = () => {
    setIsEditing(false);
    setOpenPax(false);
  };

  const handleOpenNoShow = (idd: string) => {
    setIsEditing(true);
    setIdd(idd);
    setOpenNoShow(true);
  };
  const handleCloseNoShow = () => {
    setIsEditing(false);
    setOpenNoShow(false);
  };

  const handleOpenExtras = (idd: string) => {
    setIsEditing(true);
    setIdd(idd);
    setOpenExtras(true);
  };
  const handleCloseExtras = () => {
    setIsEditing(false);
    setOpenExtras(false);
  };

  const handleOpenConductor = (idd: string) => {
    setIdd(idd);
    setOpenConductor(true);
  };

  const handleCloseConductor = () => {
    setOpenConductor(false);
  };

  return (
    <>
      <TableRow style={rowStyle}>
        <TableCell align="left" style={{ width: 80, height: 20 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>

          {primerViaje && (
            <div
              style={{
                textAlign: "center",
                borderRadius: "10px",
                marginLeft: 35,
                border: "1px solid #000",
                width: 60,
                height: 20,
                backgroundColor:
                  primerViaje.Estado === "Proximamente"
                    ? "#3F51B5"
                    : primerViaje.Estado === "Activo"
                    ? "#2196F3"
                    : primerViaje.Estado === "En curso"
                    ? "#FFC107"
                    : "transparent",
                marginTop: -30,
              }}
            >
              {primerViaje.Hora}
            </div>
          )}
        </TableCell>

        <TableCell align="left" style={{ width: 80 }}>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {Mapeo.Datos.nrt}
            {Mapeo.Datos.fechaCounterIngresada &&
              isNewService(Mapeo.Datos.fechaCounterIngresada) &&
              !Mapeo.Viaje.some((viaje: any) => viaje.Estado === "Completado") &&
              !hidden && (
                <div
                  style={{
                    ...blinkingStyle,
                    display: "flex",
                    alignItems: "center",
                    marginLeft: "10px",
                    padding: "3px 8px",
                    borderRadius: "12px",
                  }}
                >
                  <AccessTimeIcon
                    style={{
                      color: getTrafficLightColor(
                        Mapeo.Datos.fechaCounterIngresada
                      ),
                      marginRight: "5px",
                    }}
                  />
                  <span style={{ color: "#d32f2f", fontWeight: "bold" }}>
                    Servicio nuevo
                  </span>
                  <IconButton
                    size="small"
                    onClick={() => setHidden(true)}
                    style={{ marginLeft: "5px" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
          </span>
        </TableCell>

        <TableCell align="left" style={{ width: 80 }}>
          {Mapeo.Datos.nVuelo}
        </TableCell>
        <TableCell align="left" style={{ width: 80 }}>
          {Mapeo.Datos.pax}
        </TableCell>
        <TableCell align="left" style={{ width: 80 }}>
          {Math.ceil(Mapeo.DatosProveedor?.nVehiculosPro / 2) || 1}
        </TableCell>
        <TableCell component="th" scope="row" style={{ width: 30 }}>
          <IconButton
            sx={{
              bgcolor: "#52b202",
              color: "white",
              fontWeight: "bold",
              borderRadius: "20px",
              border: "1px solid 3f51b5",
              transition: "background-color 0.3s ease-in-out",
              "&:hover": {
                bgcolor: "#76ff03",
              },
            }}
            color="success"
            onClick={handleOpenViaje}
          >
            <AddCircleIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Parte colapsable */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Estados</TableCell>
                    <TableCell style={{ width: 80 }}>Fecha</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell>Tipo de vehiculo</TableCell>
                    <TableCell>Conductor</TableCell>
                    <TableCell>Pax trans</TableCell>
                    <TableCell>No Show</TableCell>
                    <TableCell>Extras</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Mapeo.Viaje.map((historyRow: any) => (
                    <TableRow key={historyRow.idd}>
                      <TableCell>
                        {historyRow?.Estado && (() => {
                          let estado = historyRow.Estado || "Pendiente";
                          let bgColor = "";
                          let borderStyle = "1px solid #000";
                          switch (estado) {
                            case "Pendiente":
                              bgColor = "#616161";
                              break;
                            case "Cancelado":
                              bgColor = "#F44336";
                              break;
                            case "Cancelado API":
                              bgColor = "#E91E63";
                              break;
                            case "Completado":
                              bgColor = "#4CAF50";
                              break;
                            case "CompletadoS":
                              bgColor = "#a2cf6e";
                              break;
                            case "Activo":
                              bgColor = "#2196F3";
                              break;
                            case "Por asignar":
                              bgColor = "white";
                              break;
                            case "En curso":
                              bgColor = "#FFC107";
                              break;
                            case "Proximamente":
                              bgColor = "#3F51B5";
                              break;
                            case "Por Validar":
                              bgColor = "#673ab7";
                              borderStyle = "5px solid #ff1744";
                              break;
                            default:
                              break;
                          }
                          return (
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <IconButton
                                sx={{
                                  width: 100,
                                  height: 25,
                                  borderRadius: 20,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: borderStyle,
                                  backgroundColor: bgColor,
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => handleOpenFechaHora(historyRow.idd)}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {historyRow.Fecha}
                                </Typography>
                              </IconButton>
                              {historyRow.DatosProveedor.StatusFechaHora ===
                                "Aceptado" && (
                                <CheckCircleOutlineIcon
                                  sx={{ color: "green", ml: "8px" }}
                                />
                              )}
                              {historyRow.DatosProveedor.StatusFechaHora ===
                                "Cancelado" && (
                                <CancelIcon sx={{ color: "red", ml: "8px" }} />
                              )}
                              {historyRow.DatosProveedor.StatusFechaHora ===
                                "En espera" && (
                                <WarningAmberIcon
                                  sx={{ color: "#f9a825", ml: "8px" }}
                                />
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>

                      <TableCell>{historyRow.Fecha}</TableCell>
                      <TableCell>{historyRow.Hora}</TableCell>
                      <TableCell>{historyRow.Origen}</TableCell>
                      <TableCell>{historyRow.Destino}</TableCell>

                      {/* Vehículo */}
                      <TableCell align="left" style={{ width: 120 }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            sx={{
                              bgcolor: "#f9a825",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "20px",
                              height: "35px",
                              width: "80px",
                              border: "1px solid #f9a825",
                              transition: "background-color 0.3s ease-in-out",
                              "&:hover": {
                                bgcolor: "#fdd835",
                              },
                            }}
                            onClick={() => handleOpenVehiculo(historyRow.idd)}
                          >
                            {historyRow.DatosProveedor.TipoDeVehiculo}
                          </IconButton>
                          {historyRow.DatosProveedor.StatusVehiculo ===
                            "Aceptado" && (
                            <CheckCircleOutlineIcon
                              sx={{ color: "green", ml: "8px" }}
                            />
                          )}
                          {historyRow.DatosProveedor.StatusVehiculo ===
                            "Cancelado" && (
                            <CancelIcon sx={{ color: "red", ml: "8px" }} />
                          )}
                          {historyRow.DatosProveedor.StatusVehiculo ===
                            "En espera" && (
                            <WarningAmberIcon
                              sx={{ color: "#f9a825", ml: "8px" }}
                            />
                          )}
                        </div>
                      </TableCell>

                      {/* Conductor */}
                      <TableCell align="left" style={{ width: 160 }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            sx={{
                              bgcolor: "#f9a825",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "20px",
                              height: "35px",
                              width: "130px",
                              border: "1px solid #f9a825",
                              transition: "background-color 0.3s ease-in-out",
                              "&:hover": {
                                bgcolor: "#fdd835",
                              },
                            }}
                            onClick={() => handleOpenConductor(historyRow.idd)}
                          >
                            {historyRow.Conductor}
                          </IconButton>
                          {historyRow.DatosProveedor.StatusConductor ===
                            "Aceptado" && (
                            <CheckCircleOutlineIcon
                              sx={{ color: "green", ml: "8px" }}
                            />
                          )}
                          {historyRow.DatosProveedor.StatusConductor ===
                            "Cancelado" && (
                            <CancelIcon sx={{ color: "red", ml: "8px" }} />
                          )}
                          {historyRow.DatosProveedor.StatusConductor ===
                            "En espera" && (
                            <WarningAmberIcon
                              sx={{ color: "#f9a825", ml: "8px" }}
                            />
                          )}
                        </div>
                      </TableCell>

                      {/* Pax */}
                      <TableCell>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            sx={{
                              bgcolor: "#f9a825",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "20px",
                              height: "35px",
                              width: "35px",
                              border: "1px solid #f9a825",
                              transition: "background-color 0.3s ease-in-out",
                              "&:hover": {
                                bgcolor: "#fdd835",
                              },
                            }}
                            onClick={() => handleOpenPax(historyRow.idd)}
                          >
                            {historyRow.DatosProveedor.PaxTrasladados}
                          </IconButton>
                          {historyRow.DatosProveedor.StatusPax === "Aceptado" && (
                            <CheckCircleOutlineIcon
                              sx={{ color: "green", ml: "8px" }}
                            />
                          )}
                          {historyRow.DatosProveedor.StatusPax === "Cancelado" && (
                            <CancelIcon sx={{ color: "red", ml: "8px" }} />
                          )}
                          {historyRow.DatosProveedor.StatusPax === "En espera" && (
                            <WarningAmberIcon
                              sx={{ color: "#f9a825", ml: "8px" }}
                            />
                          )}
                        </div>
                      </TableCell>

                      {/* No Show */}
                      <TableCell>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            sx={{
                              bgcolor: "#f9a825",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "20px",
                              height: "35px",
                              width: "35px",
                              border: "1px solid #f9a825",
                              transition: "background-color 0.3s ease-in-out",
                              "&:hover": {
                                bgcolor: "#fdd835",
                              },
                            }}
                            onClick={() => handleOpenNoShow(historyRow.idd)}
                          >
                            {historyRow.DatosProveedor.VehiculoNoShow}
                          </IconButton>
                          {historyRow.DatosProveedor.StatusNoShow ===
                            "Aceptado" && (
                            <CheckCircleOutlineIcon
                              sx={{ color: "green", ml: "8px" }}
                            />
                          )}
                          {historyRow.DatosProveedor.StatusNoShow ===
                            "Cancelado" && (
                            <CancelIcon sx={{ color: "red", ml: "8px" }} />
                          )}
                          {historyRow.DatosProveedor.StatusNoShow ===
                            "En espera" && (
                            <WarningAmberIcon
                              sx={{ color: "#f9a825", ml: "8px" }}
                            />
                          )}
                        </div>
                      </TableCell>

                      {/* Extras */}
                      <TableCell>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            sx={{
                              bgcolor: "#52b202",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "20px",
                              border: "1px solid 3f51b5",
                              height: "35px",
                              width: "35px",
                              transition: "background-color 0.3s ease-in-out",
                              "&:hover": {
                                bgcolor: "#76ff03",
                              },
                            }}
                            onClick={() => handleOpenExtras(historyRow.idd)}
                          >
                            {historyRow.DatosProveedor.VehiculoExtra}
                          </IconButton>
                          {historyRow.DatosProveedor.StatusExtra ===
                            "Aceptado" && (
                            <CheckCircleOutlineIcon
                              sx={{ color: "green", ml: "8px" }}
                            />
                          )}
                          {historyRow.DatosProveedor.StatusExtra ===
                            "Cancelado" && (
                            <CancelIcon sx={{ color: "red", ml: "8px" }} />
                          )}
                          {historyRow.DatosProveedor.StatusExtra ===
                            "En espera" && (
                            <WarningAmberIcon
                              sx={{ color: "#f9a825", ml: "8px" }}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* MODALES */}
      <AgregarFechaHora
        open={openFechaHora}
        setOpen={handleCloseFechaHora}
        folio={Mapeo}
        Idd={Idd}
        onUpdateRow={onUpdateRow}
      />
      <AgregarViajeProveedor
        open={openAgregar}
        setOpen={handleCloseViaje}
        folio={Mapeo}
        Idd={Idd}
        onUpdateRow={onUpdateRow}
      />
      <AgregarVehiculoProveedor
        open={openVehiculo}
        setOpen={handleCloseVehiculo}
        folio={Mapeo}
        Idd={Idd}
        onUpdateRow={onUpdateRow}
      />
      <AgregarPaxProveedor
        open={openPax}
        setOpen={handleClosePax}
        folio={Mapeo}
        Idd={Idd}
        onUpdateRow={onUpdateRow}
      />
      <AgregarNoShowProveedor
        open={openNoShow}
        setOpen={handleCloseNoShow}
        folio={Mapeo}
        Idd={Idd}
        onUpdateRow={onUpdateRow}
      />
      <AgregarExtraProveedor
        open={openExtras}
        setOpen={handleCloseExtras}
        folio={Mapeo}
        Idd={Idd}
        onUpdateRow={onUpdateRow}
      />
      <ModificarConductor
        open={openConductor}
        setOpen={handleCloseConductor}
        folio={Mapeo}
        Idd={Idd}
        onUpdateRow={onUpdateRow}
      />
    </>
  );
}

export default PanelDeControlProvee;
